import { combineEpics, ofType } from 'redux-observable'
import { Observable } from 'rxjs/Observable'
import { debounceTime, mergeMap, pluck } from 'rxjs/operators'

import * as actions from './actions'
import * as api from './api'
import { addhttps } from './helpers'

const fetchIntegrationStates = (action$) => {
  return action$.pipe(
    ofType(actions.fetchIntegrationStates.toString()),
    pluck('payload'),
    mergeMap(() => {
      actions.fetchIntegrationStatesPending()

      try {
        return api
          .getIntegrationStates()
          .then((response) => actions.fetchIntegrationStatesSuccess(response))
          .catch((err) => {
            return actions.fetchIntegrationStatesFailed(err)
          })
      } catch (error) {
        return Observable.of(actions.fetchIntegrationStatesFailed(error))
      }
    })
  )
}

const fetchFacebookSettings = (action$) =>
  action$.pipe(
    ofType(actions.fetchFacebookSettings.toString()),
    mergeMap(() =>
      Observable.fromPromise(api.fetchFacebookSettings())
        .map((data) => actions.fetchFacebookSettingsSuccess(data))
        .startWith(actions.fetchFacebookSettingsPending())
        .catch((err) =>
          Observable.of(actions.fetchFacebookSettingsFailed({ err }))
        )
    )
  )

const fetchStripeKeys = (action$) =>
  action$.pipe(
    ofType(actions.fetchStripeKeys.toString()),
    mergeMap(() =>
      Observable.fromPromise(api.fetchStripeKeys())
        .map((data) => actions.fetchStripeKeysSuccess(data))
        .startWith(actions.fetchStripeKeysPending())
        .catch((err) => Observable.of(actions.fetchStripeKeysFailed({ err })))
    )
  )

const fetchOutreachStatus = (action$) =>
  action$.pipe(
    ofType(actions.fetchOutreachStatus.toString()),
    mergeMap(() =>
      Observable.fromPromise(api.fetchOutreachStatus())
        .map((data) => actions.fetchOutreachStatusSuccess(data))
        .startWith(actions.fetchOutreachStatusPending())
        .catch((err) =>
          Observable.of(actions.fetchOutreachStatusFailed({ err }))
        )
    )
  )

const createAccessToken = (action$) =>
  action$.pipe(
    ofType(actions.createAccessToken.toString()),
    pluck('payload'),
    mergeMap(({ integrationName, body }) => {
      return Observable.fromPromise(
        api.createAccessToken(integrationName, body)
      )
        .map(() => actions.createAccessTokenSuccess({ integrationName }))
        .startWith(actions.createAccessTokenPending({ integrationName }))
        .catch((err) => {
          return Observable.fromPromise(
            err.json().then((readableError) => {
              const error = readableError.message
              return actions.createAccessTokenFailed({
                err: error,
                integrationName,
              })
            })
          ).catch((err) =>
            Observable.of(
              actions.createAccessTokenFailed({ err, integrationName })
            )
          )
        })
    })
  )
const createSandBoxAccessToken = (action$) =>
  action$.pipe(
    ofType(actions.createSandBoxAccessToken.toString()),
    pluck('payload'),
    mergeMap(({ integrationName, body }) => {
      return Observable.fromPromise(api.createAccessToken('salesforce', body))
        .map(() => actions.createAccessTokenSuccess({ integrationName }))
        .startWith(actions.createAccessTokenPending({ integrationName }))
        .catch((err) => {
          return Observable.fromPromise(
            err.json().then((readableError) => {
              const error = readableError.message
              return actions.createAccessTokenFailed({
                err: error,
                integrationName,
              })
            })
          ).catch((err) =>
            Observable.of(
              actions.createAccessTokenFailed({ err, integrationName })
            )
          )
        })
    })
  )

const createSlackbotToken = (action$) =>
  action$.pipe(
    ofType(actions.createSlackbotToken.toString()),
    pluck('payload'),
    mergeMap(({ code, href }) => {
      return Observable.fromPromise(api.createSlackbotToken(code, href))
        .map(() =>
          actions.createAccessTokenSuccess({ integrationName: 'slack' })
        )
        .startWith(
          actions.createAccessTokenPending({ integrationName: 'slack' })
        )
        .catch((err) => {
          return Observable.fromPromise(
            err.json().then((readableError) => {
              const error = readableError.message
              return actions.createAccessTokenFailed({
                err: error,
                integrationName: 'slack',
              })
            })
          ).catch((err) =>
            Observable.of(
              actions.createAccessTokenFailed({ err, integrationName: 'slack' })
            )
          )
        })
    })
  )

const updateIntegrationStatus = (action$) =>
  action$.pipe(
    ofType(actions.updateIntegrationState.toString()),
    pluck('payload'),
    mergeMap(({ integration, connected }) => {
      return Observable.fromPromise(
        api.updateIntegrationState({ integration, connected })
      )
        .map((response) => actions.updateIntegrationStateSuccess(response))
        .startWith(actions.updateIntegrationStatePending())
        .catch((err) =>
          Observable.of(actions.updateIntegrationStateFailed({ err }))
        )
    })
  )

const setWordpressV2Integration = (action$) =>
  action$.pipe(
    ofType(actions.setWordpressV2Integration.toString()),
    debounceTime(1000),
    pluck('payload'),
    mergeMap(({ integration, connected }) => {
      return Observable.fromPromise(
        api.updateIntegrationState({ integration, connected })
      )
        .map((response) => actions.setWordpressV2IntegrationSuccess(response))
        .startWith(actions.setWordpressV2IntegrationPending())
        .catch((err) =>
          Observable.of(actions.setWordpressV2IntegrationFailed({ err }))
        )
    })
  )

const setWordpressDomain = (action$) =>
  action$.pipe(
    ofType(actions.setWordpressDomain.toString()),
    debounceTime(600),
    pluck('payload'),
    mergeMap(({ rootUrl }) => {
      const url = addhttps(rootUrl)
      return Observable.fromPromise(api.setWordpressDomain(url))
        .map((response) => {
          return actions.setWordpressDomainSuccess(response)
        })
        .startWith(actions.setWordpressDomainPending())
        .catch((err) => {
          return Observable.of(
            actions.setWordpressDomainFailed({ err, error: true })
          )
        })
    })
  )

const fetchWordpressDomain = (action$) =>
  action$.pipe(
    ofType(actions.fetchWordpressDomain.toString()),
    mergeMap(() => {
      return Observable.fromPromise(api.fetchWordpressDomain())
        .map((response) => actions.fetchWordpressDomainSuccess(response))
        .startWith(actions.fetchWordpressDomainPending())
        .catch((err) =>
          Observable.of(actions.fetchWordpressDomainFailed({ err }))
        )
    })
  )

const getLeanDataAuthStatus = (action$) =>
  action$.pipe(
    ofType(actions.getLeanDataAuthStatus.toString()),
    mergeMap(() => {
      return Observable.fromPromise(api.getLeanDataAuthorizationStatus())
        .map((res) => actions.getLeanDataAuthStatusSuccess({ authStatus: res }))
        .startWith(actions.getLeanDataAuthStatusPending())
        .catch(() =>
          Observable.of(
            actions.getLeanDataAuthStatusFailed({
              authStatus: 'UNSUCCESSFUL_API_REQUEST',
            })
          )
        )
    })
  )

const epic = combineEpics(
  fetchIntegrationStates,
  fetchOutreachStatus,
  fetchFacebookSettings,
  updateIntegrationStatus,
  createAccessToken,
  createSlackbotToken,
  setWordpressV2Integration,
  setWordpressDomain,
  fetchWordpressDomain,
  createSandBoxAccessToken,
  fetchStripeKeys,
  getLeanDataAuthStatus
)
export default epic
